import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const tiers = [
  {
    label: "Standard Monthly",
    price: "$4,250",
    period: "/ month",
    blurb: "Utilities included for your first month. Tenant covers utilities after month 1.",
    features: [
      "Minimum 90 days",
      "Utilities INCLUDED first month",
      "Tenant pays utilities after month 1",
      "Fully furnished, move-in ready",
    ],
    highlight: false,
  },
  {
    label: "Premium All-Inclusive",
    price: "$4,500",
    period: "/ month",
    blurb: "Zero hassle. Every utility covered the entire length of your stay.",
    features: [
      "Minimum 90 days",
      "ALL utilities included entire stay",
      "No bills to set up or transfer",
      "Fully furnished, move-in ready",
    ],
    highlight: true,
  },
];

const PricingSection = () => {
  return (
    <section className="section-lg bg-white" data-testid="pricing-section">
      <div className="container-premium">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="flex justify-center mb-4">
            <div className="gold-line" />
          </div>
          <span className="label-sm text-[#C9A962]">Straightforward Pricing</span>
          <h2 className="headline-lg text-[#0A1628] mt-4 mb-6">
            Two Simple Options — Pick What Fits
          </h2>
          <p className="body-md text-[#0A1628]/60 max-w-2xl mx-auto">
            No nightly rates. No surprise fees. Minimum stay 90 days, maximum up to 9 months.
            Flexible move-in dates available.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {tiers.map((tier, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative p-8 lg:p-10 ${
                tier.highlight ? "bg-[#0A1628] text-white" : "bg-[#F7F3ED] text-[#0A1628]"
              }`}
              data-testid={`pricing-card-${i}`}
            >
              {tier.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C9A962] text-[#0A1628] px-4 py-1 text-xs font-body font-semibold uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  Premium Convenience
                </div>
              )}

              <p className={`label-sm text-[#C9A962] mb-3`}>
                {tier.highlight ? "Option 2" : "Option 1"}
              </p>
              <h3 className="font-display text-2xl font-medium mb-2">{tier.label}</h3>
              <p className={`font-body text-sm mb-6 ${tier.highlight ? "text-white/70" : "text-[#0A1628]/60"}`}>
                {tier.blurb}
              </p>

              <div className="flex items-baseline gap-1 mb-8 pb-6 border-b border-dashed border-current/20">
                <span className="font-display text-5xl lg:text-6xl font-medium">{tier.price}</span>
                <span className={`font-body text-sm ml-1 ${tier.highlight ? "text-white/60" : "text-[#0A1628]/50"}`}>
                  {tier.period}
                </span>
              </div>

              <ul className="space-y-3 mb-10">
                {tier.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#C9A962] flex-shrink-0 mt-0.5" />
                    <span className={`font-body text-sm ${tier.highlight ? "text-white/85" : "text-[#0A1628]/75"}`}>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="space-y-2">
                <Link to="/contact" className="block">
                  <Button
                    className={`w-full rounded-none py-5 font-medium ${
                      tier.highlight
                        ? "bg-[#C9A962] text-[#0A1628] hover:bg-[#B8954D]"
                        : "bg-[#0A1628] text-white hover:bg-[#1E3A5F]"
                    }`}
                    data-testid={`pricing-cta-${i}`}
                  >
                    Check Availability
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact" className="block">
                  <button className={`w-full text-sm font-body underline underline-offset-4 py-2 ${tier.highlight ? "text-white/70 hover:text-white" : "text-[#0A1628]/60 hover:text-[#0A1628]"}`}>
                    Request Dates
                  </button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-12"
        >
          <p className="font-body text-sm text-[#0A1628]/60 max-w-xl mx-auto">
            <span className="font-semibold text-[#0A1628]">Maximum stay: up to 9 months.</span>{" "}
            Flexible move-in dates. Ask about custom terms for your exact timeline.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
